import { useState } from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { GridWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import {
    IHeaderParams, GridApi, ColumnApi, GridReadyEvent,
    ValueGetterParams, FirstDataRenderedEvent, ICellRendererParams, GridSizeChangedEvent, SortChangedEvent, ViewportChangedEvent, ColDef
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IAntiBodyTransactionDetails } from '@nkr/models/index';

interface IProps {
    reportList: IAntiBodyTransactionDetails[]
}

const VrlTransDetailGridAntibody = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const [columns, setColumns] = useState<ColDef[]>([
        {
            colId: 'Rank',
            field: 'rank',
            headerName: 'Row #',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Row #
                </SortableHeaderComponent>,
            width: 55,
            cellClass: 'center-list-cell',
            suppressSizeToFit: true,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'orderId',
            field: 'orderId',
            headerName: 'Order ID',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Order ID
                </SortableHeaderComponent>,
            width: 60,
            cellClass: 'center-list-cell',
            sortable: true,
            suppressSizeToFit: true
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient Center
                </SortableHeaderComponent>,
            minWidth: 120,
            sortable: true,
            resizable: true
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient Alias
                </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let { recipientAlias, recipientId, recipientCenterId } = props.data;

                return (recipientId > 0
                    ? <a target={'_blank'} href={`center/${recipientCenterId}/recipient/${recipientId}/profile/general`}>{recipientAlias}</a>
                    : <span>{recipientAlias}</span>)
            },
            minWidth: 140,
            sortable: true,
            resizable: true
        },
        {
            colId: 'requested',
            field: 'requested',
            headerName: 'Requested',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Requested<br />Date
                </SortableHeaderComponent>,
            maxWidth: 140,
            sortable: true,
            suppressSizeToFit: true,
            cellRenderer: (data) => {
                if (data.value !== null)
                    return (moment(data.value).format('MM/DD/YYYY'))
                else
                    return '--';
            }
        },
        {
            colId: 'completed',
            field: 'completed',
            headerName: 'Completed',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Completed<br />Date
                </SortableHeaderComponent>,
            maxWidth: 140,
            sortable: true,
            sort: 'desc',
            suppressSizeToFit: true,
            cellRenderer: (data) => {
                if (data.value !== null)
                    return (moment(data.value).format('MM/DD/YYYY'))
                else
                    return '--';
            }
        }
    ]);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi)

        e.api.sizeColumnsToFit();
    };

    const onViewportChanged = (e: ViewportChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        columnApi.setColumnsVisible(columnsToShow, true);
        columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        gridApi.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    return (
        <>
            <div className='row'>
                <div className='col-sm-12'>
                    <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                        <AgGridReact
                            onGridReady={onGridReady}
                            onGridSizeChanged={onGridSizeChanged}
                            onFirstDataRendered={onFirstDataRendered}
                            enableCellTextSelection={true}
                            onViewportChanged={onViewportChanged}
                            enableRangeSelection={true}
                            columnDefs={columns}
                            rowData={props.reportList}
                            headerHeight={60}
                            noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                            suppressCellSelection={true}
                            onSortChanged={onSortChanged}
                        />
                    </GridWrapper>
                </div>
            </div>
        </>
    );
}

export default VrlTransDetailGridAntibody;